import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Factory, AlertTriangle, Ban } from 'lucide-react';
import FieldInput from './FieldInput';

// Track A: pick one of the five industries, answer its question set. The rules that would refer
// or decline are printed beside the questions so nothing fires that the broker was not told about.
export default function IndustryQuestions({ api, classIds, entityId, form, setForm, recordEdit }) {
  const [sets, setSets] = useState(null);
  useEffect(() => {
    let live = true;
    const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : '';
    api.get(`/question-sets${qs}`).then(r => {
      if (!live) return;
      setSets(r.data);
      if (!form.industry && r.data.suggested_industry) setForm(p => ({ ...p, industry: r.data.suggested_industry }));
    }).catch(() => setSets({ industries: [] }));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, entityId]);

  if (!sets) return null;
  const industry = sets.industries.find(i => i.id === form.industry);
  const questions = industry ? classIds.flatMap(c => (industry.classes[c]?.questions || []).map(q => ({ ...q, _class: c }))) : [];
  const seen = new Set();
  const uniqueQuestions = questions.filter(q => !seen.has(q.name) && seen.add(q.name));
  const rules = industry ? classIds.flatMap(c => industry.classes[c]?.rules || []) : [];

  return (
    <Card className="p-5 rounded-sm bg-white space-y-4" style={{ border: '1px solid rgba(136,150,168,0.2)', borderLeft: '3px solid #1A4C7C' }} data-testid="industry-questions-card">
      <div className="flex items-center gap-2">
        <Factory className="w-4 h-4" style={{ color: '#1A4C7C' }} />
        <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#0D1B2A' }}>Industry</h3>
        {sets.suggested_industry && <span className="text-[10px] ml-auto" style={{ color: '#5C6B7A' }}>Suggested from the register — confirm or change</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {sets.industries.map(i => {
          const on = form.industry === i.id;
          return (
            <button type="button" key={i.id} data-testid={`industry-${i.id}`}
              onClick={() => { recordEdit?.('industry', form.industry, i.id); setForm(p => ({ ...p, industry: i.id })); }}
              className="text-left p-3 rounded-sm border transition-all hover:-translate-y-[1px]"
              style={{ borderColor: on ? '#1A4C7C' : 'rgba(136,150,168,0.25)', background: on ? 'rgba(26,76,124,0.06)' : '#fff', boxShadow: on ? '0 0 0 1px #1A4C7C' : 'none' }}>
              <p className="text-[10px] font-mono" style={{ color: '#5C6B7A' }}>{i.number}</p>
              <p className="text-xs font-semibold leading-snug" style={{ color: on ? '#1A4C7C' : '#0D1B2A' }}>{i.name}</p>
            </button>
          );
        })}
      </div>
      {!industry && <p className="text-xs" style={{ color: '#B27516' }} data-testid="industry-unselected-note">Pick the industry — its question set and referral rules load beneath.</p>}
      {industry && (
        <>
          <p className="text-[11px]" style={{ color: '#5C6B7A' }}>{industry.examples}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="industry-question-fields">
            {uniqueQuestions.map(q => <FieldInput key={q.name} field={q} form={form} setForm={setForm} recordEdit={recordEdit} prefix="industry-field-" />)}
          </div>
          {rules.length > 0 && (
            <div className="rounded-sm p-3" style={{ background: '#F5F7FA' }} data-testid="industry-rules">
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#5C6B7A' }}>Rules on this industry — {rules.length}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                {rules.map(r => (
                  <p key={r.id} className="text-[11px] flex items-start gap-1.5" style={{ color: '#0D1B2A' }} data-testid={`industry-rule-${r.id}`}>
                    {r.action === 'AUTO_DECLINE' ? <Ban className="w-3 h-3 mt-[2px] shrink-0" style={{ color: '#E24B4A' }} /> : <AlertTriangle className="w-3 h-3 mt-[2px] shrink-0" style={{ color: '#E8A020' }} />}
                    <span><span className="font-mono text-[10px]" style={{ color: '#5C6B7A' }}>{r.id}</span> {r.desc}</span>
                  </p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
